import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import MessageIcon from "@mui/icons-material/Message";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { BOTTOM_NAV, FONT_SIZE, INK, LETTER_SPACING, NEUTRAL } from "../../../theme/tokens";
import { MobileStaffingView } from "../../../utils/models";
import { staffingClassicPath } from "../../../utils/staffingRoutes";
import AdminDrawer from "../../header/AdminDrawer";
import { menuRowStyles } from "../../header/utils";
import { WageRevealButton } from "../../header/WageRevealButton";
import { useGapsViewPreference } from "./useGapsViewPreference";

type Props = {
	open: boolean;
	onClose: () => void;
	/** The day the shell is showing (`yyyy-MM-dd`) — the quick actions act on it. */
	date: string;
	/** Opens full-screen Annie, forwarded to the menu so the sheet offers exactly what the hamburger does. */
	onOpenAnnie?: () => void;
	/** Opens the call-in sheet on the home — the bar owns this, because only it knows whether the home is on screen. */
	onLogCallIn: () => void;
	/** Opens the add-someone sheet on the home (which asks which shift first), for the same reason. */
	onAddToShift: () => void;
};

type QuickAction = {
	key: string;
	label: string;
	icon: ReactElement;
	onClick: () => void;
};

/**
 * The gaps-first shell's "More" slot.
 *
 * It opens the REAL admin menu (`AdminDrawer`, anchored bottom) rather than a menu of its own. The
 * bottom bar took the hamburger's place, so "More" has to lead somewhere that stays in step with it —
 * a second list of the same destinations would go stale the first time anyone added a row to one of
 * them, and the two would disagree without either looking wrong.
 *
 * All this component adds is the QUICK ACTIONS block on top: the create actions the classic screen kept
 * in a speed-dial that the gaps shell does not render.
 */
export const GapsMoreSheet: FC<Props> = ({ open, onClose, date, onOpenAnnie, onLogCallIn, onAddToShift }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { userInfo } = useAuth();
	const { setStoredView } = useGapsViewPreference();

	const departmentId = chosenDepartment.department_id;

	const go = (path: string, action: string): void => {
		track("gaps_more_action_clicked", { action, department_id: departmentId });
		onClose();
		navigate(path);
	};

	const quickActions: QuickAction[] = [
		{
			key: "add-to-shift",
			label: t("gaps.more.addToShift"),
			icon: <LocalHospitalOutlinedIcon sx={menuRowStyles.icon} />,
			// A sheet on the home, like Log a call-in: the shifts to pick from are the day's gaps, short first.
			onClick: () => {
				track("gaps_more_action_clicked", { action: "add_to_shift", department_id: departmentId });
				onAddToShift();
			},
		},
		{
			key: "log-call-in",
			label: t("gaps.more.logCallIn"),
			icon: <EventBusyOutlinedIcon sx={menuRowStyles.icon} />,
			// A sheet on the home rather than a page: "who called in" is answered against the day's roster, and the
			// cover prompt after it has to land on the day's gaps.
			onClick: () => {
				track("gaps_more_action_clicked", { action: "log_call_in", department_id: departmentId });
				onLogCallIn();
			},
		},
		{
			key: "send-sms",
			label: t("gaps.more.sendSms"),
			icon: <MessageIcon sx={menuRowStyles.icon} />,
			onClick: () => go(`/${departmentId}/send-message`, "send_sms"),
		},
	];

	const handleBackToClassic = (): void => {
		track("gaps_view_exited", { department_id: departmentId });
		setStoredView(MobileStaffingView.Classic);
		onClose();
		navigate(staffingClassicPath(departmentId, date));
	};

	const quickActionsBlock = (
		// A rule under the block, matching the identity row above it: these are things to DO, and what
		// follows is places to go. Without the separation the four actions read as the top of one long menu.
		<Box data-testid="gaps-more-sheet-body" sx={{ pt: 1, pb: 2, borderBottom: `1px solid ${NEUTRAL[300]}` }}>
			<Typography
				component="h3"
				sx={{
					fontSize: FONT_SIZE.LABEL_XS,
					letterSpacing: LETTER_SPACING.LABEL,
					textTransform: "uppercase",
					color: INK.MUTED,
					fontWeight: 700,
				}}
			>
				{t("gaps.more.quickActions")}
			</Typography>
			<List sx={{ p: 0 }}>
				{quickActions.map(({ key, label, icon, onClick }) => (
					<ListItemButton key={key} data-testid={`gaps-more-action-${key}`} onClick={onClick} sx={{ borderRadius: 1 }}>
						<ListItemIcon sx={menuRowStyles.iconContainer}>{icon}</ListItemIcon>
						<ListItemText primary={label} sx={menuRowStyles.text} />
					</ListItemButton>
				))}
				{/* The header's own control, in its row variant — not a copy of it. The permission gate, the
				    ask-before-revealing asymmetry and the confirmation dialog are the parts that must never
				    exist twice, and they live there. This sheet only says where the row goes and what to track. */}
				<WageRevealButton
					variant="menuRow"
					dataTestId="gaps-more-toggle-wages"
					onToggle={(revealWage) => track("gaps_more_wages_toggled", { department_id: departmentId, reveal_wage: revealWage })}
				/>
				<ListItemButton data-testid="gaps-more-back-to-classic" onClick={handleBackToClassic} sx={{ borderRadius: 1 }}>
					<ListItemIcon sx={menuRowStyles.iconContainer}>
						<ViewListOutlinedIcon sx={menuRowStyles.icon} />
					</ListItemIcon>
					<ListItemText primary={t("gaps.more.backToClassic")} sx={menuRowStyles.text} />
				</ListItemButton>
			</List>
		</Box>
	);

	return (
		<AdminDrawer
			dataTestId="gaps-more-sheet"
			open={open}
			setMenuOpen={(isOpen) => {
				if (!isOpen) onClose();
			}}
			userInfo={userInfo}
			// Header fetches the avatar into its own state; the sheet has no copy, and the menu already
			// falls back to an initial avatar rather than showing a broken image.
			profileImage={null}
			onOpenAnnie={onOpenAnnie}
			anchor="bottom"
			bottomOffset={BOTTOM_NAV.TOTAL_HEIGHT_CSS}
			prepend={quickActionsBlock}
		/>
	);
};
